import React from "react";
import { graphql, Link } from "gatsby";
import Layout from "../components/layout";
import { Container, Row, Col } from "react-bootstrap";
import Seo from "../components/seo";
import FAQ from "../components/faq";
import ModalVideo from "../components/modalVideo";
import PreCollegeCourseFilter from "../filters/preCollegeCourseFilter";
import UnderGradCourseFilter from "../filters/underGradCourseFilter";
import { MARKS, INLINES, BLOCKS } from "@contentful/rich-text-types";
import { renderRichText } from "gatsby-source-contentful/rich-text";

const PageTemplate = ({ data, pageContext }) => {
  const pageContent = data.contentfulSuPage || {};
  const { nodes: pages = [] } = data.allContentfulSuPage || {};
  const { nodes: suContents = [] } = data.allContentfulSuContent || {};

  const currentPage = pageContent;

  // *********************************** FUNCTION TO BUILD URL PATHS ***********************************
  function getEntryWithId(pageId) {
    const matchingIdSuPage = data.allContentfulSuPage.nodes.find(
      (node) => node.contentful_id === pageId
    );

    const matchingIdPdf = data.allContentfulPdf.nodes.find(
      (node) => node.contentful_id === pageId
    );

    const matchingIdSuCourse = data.allContentfulSuCourse.nodes.find(
      (node) => node.contentful_id === pageId
    );

    const matchingId = matchingIdPdf || matchingIdSuPage || matchingIdSuCourse;

    if (!matchingId) return;
    const matchingEntry =
      matchingId.pageSlug || matchingId.pdfSlug || matchingId.courseSlug;
    const matchingType = matchingId.__typename;

    const buildUrlPath = (items, slug, type) => {
      // Handle PDF and Course cases first
      if (type === "ContentfulPdf") {
        return `${slug}.pdf`;
      }

      if (type === "ContentfulSuCourse") {
        return `courses/${slug}`;
      }

      // Continue with existing navigation logic
      for (const item of items) {
        if (item.navSlug === slug) {
          return `${item.navSlug}`;
        }
        if (item.navNextLevel) {
          const nextLevelPath = buildUrlPath(item.navNextLevel, slug, type);
          if (nextLevelPath !== "") {
            return `${item.navSlug}/${nextLevelPath}`;
          }
        }
      }

      return "";
    };

    //const urlPath = buildUrlPath(data.allContentfulCrNavGroup.nodes, matchingEntry, matchingType);
    const urlPath = buildUrlPath(
      data.allContentfulSuNavGroup.nodes,
      matchingEntry,
      matchingType
    );

    return `/${urlPath}`;
  }

  // ***************************** OPTIONS FOR RICH TEXT RENDERING *****************************
  const options = {
    renderText: (text) => {
      return text.split("\n").reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, []);
    },
    renderMark: {
      [MARKS.BOLD]: (text) => <strong className="font-bold">{text}</strong>,
    },
    renderNode: {
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        const referencedEntry = getEntryWithId(node.data.target.sys.id);
        if (!referencedEntry) return children;
        const isPdf = referencedEntry?.endsWith(".pdf");
        return isPdf ? (
          <a href={`${referencedEntry}`} target="_blank" rel="noreferrer">
            {children}
          </a>
        ) : (
          <Link to={`${referencedEntry}`}>{children}</Link>
        );
      },
      [INLINES.HYPERLINK]: (node, children) => {
        const referencedEntry = node.data.uri;
        return (
          <Link to={`${referencedEntry}`} target="_blank">
            {children}
          </Link>
        );
      },
      [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
      [BLOCKS.TABLE]: (node, children) => (
        <table className="other-table">{children}</table>
      ),
    },
  };

  const layoutMap = {
    "1 Column": [{ xs: 12 }],
    "2 Column - 50/50": [
      { xs: 12, md: 6 },
      { xs: 12, md: 6 },
    ],
    "2 Column - 33/66": [
      { xs: 8, md: 4 },
      { xs: 12, md: 8 },
    ],
    "2 Column - 66/33": [
      { xs: 12, md: 8 },
      { xs: 8, md: 4 },
    ],
    "2 Column - 25/75": [
      { xs: 8, sm: 6, md: 3 },
      { xs: 12, md: 9 },
    ],
    "2 Column - 75/25": [
      { xs: 12, md: 8, xl: 9 },
      { xs: 8, sm: 6, md: 4, xl: 3 },
    ],
    "3 Column - 33/33/33": [
      { xs: 6, md: 4 },
      { xs: 6, md: 4 },
      { xs: 6, md: 4 },
    ],
    "3 Column - 50/25/25": [
      { xs: 12, sm: 6 },
      { xs: 6, sm: 3 },
      { xs: 6, sm: 3 },
    ],
    "3 Column - 25/50/25": [
      { xs: 6, sm: 3 },
      { xs: 12, sm: 6 },
      { xs: 6, sm: 3 },
    ],
    "3 Column - 25/25/50": [
      { xs: 6, sm: 3 },
      { xs: 6, sm: 3 },
      { xs: 12, sm: 6 },
    ],
    "4 Column - 25/25/25/25": [
      { xs: 12, sm: 6, md: 3 },
      { xs: 12, sm: 6, md: 3 },
      { xs: 12, sm: 6, md: 3 },
      { xs: 12, sm: 6, md: 3 },
    ],
  };

  const renderColumnContent = (colContentArray, options) => {
    // If colContentArray is not an array, wrap it in an array
    const contentArray = Array.isArray(colContentArray)
      ? colContentArray
      : [colContentArray];

    return contentArray.map((content) => (
      <div id={content.styleId} key={content.styleId}>
        {/* Conditionally render background-shapes div if styleId starts with "course-finder" */}
        {content.styleId && content.styleId.indexOf("course-finder" && "cross-linked") === 0 && (
          <div className="background-shapes">
            <div class="shape"></div>
            <div class="shape"></div>
            <div class="shape"></div>
          </div>
        )}

        {/* Render content title if it exists */}
        {content.displayContentName && <h3>{content.contentTitle}</h3>}

        {/* Render content body if it exists */}
        {content.contentBody && (
          <>{renderRichText(content.contentBody, options)}</>
        )}

        {/* Check if there's an image and render it */}
        {content.image && content.image.url && (
          <div className="image-container">
            <img
              src={content.image.url}
              alt={content.captionRichText || "Image"}
            />
            {content.showName && <h3>{content.name}</h3>}
            {content.captionRichText && (
              <p className="image-caption">
                {renderRichText(content.captionRichText, options)}
              </p>
            )}
          </div>
        )}

        {/* Check if there's an FAQ and render it */}
        {content.questionsAndAnswers && (
          <>
            <div className="faq-container">
              <FAQ
                content={content.questionsAndAnswers}
                data={data}
                title={content.showName ? content.title : undefined}
              />
            </div>
          </>
        )}

        {/* Check if there's a Modal Video and render it */}
        {content.videoId && (
          <div>
            <ModalVideo
              id={content.id}
              title={content.videoName}
              youtubeID={content.videoId}
              caption={content.videoCaption}
              videoImageThumbnail={content.videoImageThumbnail}
            />
          </div>
        )}
      </div>
    ));
  };

  const GridLayout = ({ currentPage, options }) => {
    return (
      <>
        {currentPage.gridSystem.map((gridItem) => {
          const columns = layoutMap[gridItem.layout] || [];

          // Create an array of the columns in the desired order
          const orderedColumns = [
            { key: "singleColumn", content: gridItem.singleColumn },
            { key: "leftColumn", content: gridItem.leftColumn },
            { key: "middleColumn", content: gridItem.middleColumn },
            { key: "middleLeftColumn", content: gridItem.middleLeftColumn },
            { key: "middleRightColumn", content: gridItem.middleRightColumn },
            { key: "rightColumn", content: gridItem.rightColumn },
          ];

          // Filter out undefined or null columns (those that don't exist)
          const filteredColumns = orderedColumns.filter(
            (column) => column.content
          );

          return (
            <Container fluid key={gridItem.id} id={gridItem.styleId}>
              <Container>
                {gridItem.showTitle && <h2>{gridItem.name}</h2>}
                <Row>
                  {filteredColumns.map((column, index) => (
                    <Col {...(columns[index] || {})} key={column.key}>
                      {/* Render the content of the column */}
                      {renderColumnContent(column.content, options)}
                    </Col>
                  ))}
                </Row>
              </Container>
            </Container>
          );
        })}
      </>
    );
  };

  return (
    <>
      <Layout>
        <GridLayout currentPage={currentPage} options={options} />

        {/* @@@@@@@@@@ THIS WILL SHOW PreCollegeCourseFilter only if the suPage slug is /pre-college-course-finder/ @@@@@@@@@@*/}
        {pageContent.pageSlug === "pre-college-course-finder" && (
          <PreCollegeCourseFilter />
        )}

        {/* @@@@@@@@@@ THIS WILL SHOW UnderGradCourseFilter only if the suPage slug is /undergraduate-course-finder/ @@@@@@@@@@*/}
        {pageContent.pageSlug === "undergraduate-course-finder" && (
          <UnderGradCourseFilter />
        )}
      </Layout>
    </>
  );
};

export const query = graphql`
  query($id: String!) {
    contentfulSuPage(id: { eq: $id }) {
      pageTitle
      pageSlug
      id
      contentful_id
      __typename
      gridSystem {
        ... on ContentfulGridOneColumn {
          id
          styleId
          name
          showTitle
          layout
          singleColumn {
            ... on ContentfulSuContent {
              id
              contentTitle
              displayContentName
              styleId
              contentBody {
                raw
              }
            }
            ... on ContentfulBlockImage {
              id
              name
              showName
              styleId
              image {
                url
              }
              captionRichText {
                raw
              }
            }
            ... on ContentfulFaq {
              id
              title
              styleId
              questionsAndAnswers {
                id
                styleId
                faq {
                  title
                  showName
                }
                question
                answer {
                  raw
                }
              }
            }
            ... on ContentfulModalVideo {
              id
              styleId
              videoName
              videoId
              videoCaption {
                raw
              }
              videoImageThumbnail {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
        }
        ... on ContentfulGridTwoColumn {
          id
          styleId
          name
          showTitle
          layout
          leftColumn {
            ... on ContentfulSuContent {
              id
              contentTitle
              displayContentName
              styleId
              contentBody {
                raw
              }
            }
            ... on ContentfulBlockImage {
              id
              styleId
              name
              showName
              image {
                url
              }
              captionRichText {
                raw
              }
            }
            ... on ContentfulFaq {
              id
              styleId
              title
              showName
              questionsAndAnswers {
                id
                styleId
                faq {
                  title
                  showName
                }
                question
                answer {
                  raw
                }
              }
            }
            ... on ContentfulModalVideo {
              id
              styleId
              videoName
              videoId
              videoCaption {
                raw
              }
              videoImageThumbnail {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
          rightColumn {
            ... on ContentfulSuContent {
              id
              contentTitle
              displayContentName
              styleId
              contentBody {
                raw
              }
            }
            ... on ContentfulBlockImage {
              id
              styleId
              name
              showName
              image {
                url
              }
              captionRichText {
                raw
              }
            }
            ... on ContentfulFaq {
              id
              styleId
              title
              showName
              questionsAndAnswers {
                id
                styleId
                faq {
                  title
                  showName
                }
                question
                answer {
                  raw
                }
              }
            }
            ... on ContentfulModalVideo {
              id
              styleId
              videoName
              videoId
              videoCaption {
                raw
              }
              videoImageThumbnail {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
        }
        ... on ContentfulGridThreeColumn {
          id
          styleId
          name
          showTitle
          styleId
          layout
          leftColumn {
            ... on ContentfulSuContent {
              id
              contentTitle
              displayContentName
              styleId
              contentBody {
                raw
              }
            }
            ... on ContentfulBlockImage {
              id
              styleId
              name
              showName
              image {
                url
              }
              captionRichText {
                raw
              }
            }
            ... on ContentfulFaq {
              id
              styleId
              questionsAndAnswers {
                id
                styleId
                faq {
                  title
                  showName
                }
                question
                answer {
                  raw
                }
              }
            }
            ... on ContentfulModalVideo {
              id
              styleId
              videoName
              videoId
              videoCaption {
                raw
              }
              videoImageThumbnail {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
          middleColumn {
            ... on ContentfulSuContent {
              id
              contentTitle
              displayContentName
              styleId
              contentBody {
                raw
              }
            }
            ... on ContentfulBlockImage {
              id
              styleId
              name
              showName
              image {
                url
              }
              captionRichText {
                raw
              }
            }
            ... on ContentfulFaq {
              id
              styleId
              questionsAndAnswers {
                id
                styleId
                faq {
                  title
                  showName
                }
                question
                answer {
                  raw
                }
              }
            }
            ... on ContentfulModalVideo {
              id
              styleId
              videoName
              videoId
              videoCaption {
                raw
              }
              videoImageThumbnail {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
          rightColumn {
            ... on ContentfulSuContent {
              id
              contentTitle
              displayContentName
              styleId
              contentBody {
                raw
              }
            }
            ... on ContentfulBlockImage {
              id
              styleId
              name
              showName
              image {
                url
              }
              captionRichText {
                raw
              }
            }
            ... on ContentfulFaq {
              id
              styleId
              questionsAndAnswers {
                id
                styleId
                faq {
                  title
                  showName
                }
                question
                answer {
                  raw
                }
              }
            }
            ... on ContentfulModalVideo {
              id
              styleId
              videoName
              videoId
              videoCaption {
                raw
              }
              videoImageThumbnail {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
        }
        ... on ContentfulGridFourColumn {
          id
          styleId
          name
          showTitle
          layout
          leftColumn {
            ... on ContentfulSuContent {
              id
              contentTitle
              displayContentName
              styleId
              contentBody {
                raw
              }
            }
            ... on ContentfulBlockImage {
              id
              styleId
              name
              showName
              image {
                url
              }
              captionRichText {
                raw
              }
            }
            ... on ContentfulFaq {
              id
              styleId
              questionsAndAnswers {
                id
                styleId
                faq {
                  title
                  showName
                }
                question
                answer {
                  raw
                }
              }
            }
            ... on ContentfulModalVideo {
              id
              styleId
              videoName
              videoId
              videoCaption {
                raw
              }
              videoImageThumbnail {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
          middleLeftColumn {
            ... on ContentfulSuContent {
              id
              contentTitle
              displayContentName
              styleId
              contentBody {
                raw
              }
            }
            ... on ContentfulBlockImage {
              id
              styleId
              name
              showName
              image {
                url
              }
              captionRichText {
                raw
              }
            }
            ... on ContentfulFaq {
              id
              styleId
              questionsAndAnswers {
                id
                styleId
                faq {
                  title
                  showName
                }
                question
                answer {
                  raw
                }
              }
            }
            ... on ContentfulModalVideo {
              id
              styleId
              videoName
              videoId
              videoCaption {
                raw
              }
              videoImageThumbnail {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
          middleRightColumn {
            ... on ContentfulSuContent {
              id
              contentTitle
              displayContentName
              styleId
              contentBody {
                raw
              }
            }
            ... on ContentfulBlockImage {
              id
              styleId
              name
              showName
              image {
                url
              }
              captionRichText {
                raw
              }
            }
            ... on ContentfulFaq {
              id
              styleId
              questionsAndAnswers {
                id
                styleId
                faq {
                  title
                  showName
                }
                question
                answer {
                  raw
                }
              }
            }
            ... on ContentfulModalVideo {
              id
              styleId
              videoName
              videoId
              videoCaption {
                raw
              }
              videoImageThumbnail {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
          rightColumn {
            ... on ContentfulSuContent {
              id
              contentTitle
              displayContentName
              styleId
              contentBody {
                raw
              }
            }
            ... on ContentfulBlockImage {
              id
              styleId
              name
              showName
              image {
                url
              }
              captionRichText {
                raw
              }
            }
            ... on ContentfulFaq {
              id
              styleId
              questionsAndAnswers {
                id
                styleId
                faq {
                  title
                  showName
                }
                question
                answer {
                  raw
                }
              }
            }
            ... on ContentfulModalVideo {
              id
              styleId
              videoName
              videoId
              videoCaption {
                raw
              }
              videoImageThumbnail {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
        }
      }
    }
    allContentfulSuCourse {
      nodes {
        id
        styleId
        courseSlug
        contentful_id
        __typename
      }
    }

    allContentfulPdf(filter: { site: { eq: "Summer" } }) {
      nodes {
        documentTitle
        id
        contentful_id
        pdfSlug
        site
        __typename
        pdfDocument {
          description
          file {
            fileName
            url
          }
        }
      }
    }

    allContentfulSuPage {
      nodes {
        id
        contentful_id
        __typename
        pageSlug
      }
    }

    allContentfulSuContent {
      nodes {
        id
        contentTitle
      }
    }
    allContentfulSuNavItem {
      nodes {
        navSlug
        navItemName
        navNextLevel {
          navSlug
          navNextLevel {
            navSlug
            navNextLevel {
              navSlug
              navNextLevel {
                navSlug
              }
            }
          }
        }
      }
    }
    allContentfulSuNavGroup {
      nodes {
        navGroupName
        navSlug
        contentful_id
        navNextLevel {
          navSlug
          navItemName
          contentful_id
          navNextLevel {
            navSlug
            contentful_id
            navNextLevel {
              navSlug
              contentful_id
            }
          }
        }
      }
    }
  }
`;

export default PageTemplate;
